/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Fragment, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import type { ShopperProducts } from '@/scapi';
import { ChevronLeft } from 'lucide-react';
import { routes, routeHref } from '@/route-paths';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { getCategoryPathFromTree, getMobileParentCategoryItem } from '@/lib/category/breadcrumb-utils';

export type CategoryBreadcrumbPageType = 'category' | 'product';

export default function CategoryBreadcrumbs({
    category,
    page = 'category',
}: {
    category: ShopperProducts.schemas['Category'];
    page?: CategoryBreadcrumbPageType;
}): ReactElement | null {
    const { t } = useTranslation('category');
    const isProductPage = page === 'product';
    const categoryPath = getCategoryPathFromTree(category);
    const mobileParent = getMobileParentCategoryItem(categoryPath, isProductPage);

    if (categoryPath.length === 0 && !isProductPage) {
        return null;
    }

    return (
        <>
            {mobileParent ? (
                <nav
                    aria-label={t('breadcrumbs.label')}
                    className="mb-6 lg:hidden"
                    data-testid="category-breadcrumbs-mobile">
                    <Link
                        to={routeHref(routes.category, { categoryId: mobileParent.id })}
                        className="inline-flex items-center gap-1 text-sm leading-5 text-muted-foreground transition-colors hover:text-foreground">
                        <ChevronLeft className="size-4 shrink-0" aria-hidden />
                        {mobileParent.name}
                    </Link>
                </nav>
            ) : null}

            <Breadcrumb
                aria-label={t('breadcrumbs.label')}
                className="mb-6 hidden lg:block"
                data-testid="category-breadcrumbs-desktop">
                <BreadcrumbList className="gap-x-2.5 gap-y-1 text-sm leading-5">
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to={routes.home} className="text-muted-foreground hover:text-foreground">
                                {t('breadcrumbs.home')}
                            </Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>

                    {categoryPath.map((item, index) => {
                        const isLastCategory = index === categoryPath.length - 1;
                        const isCurrentCategory = isLastCategory && !isProductPage;

                        // Skip the first category
                        if (index === 0) {
                            return null;
                        }

                        return (
                            <Fragment key={item.id}>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    {isCurrentCategory ? (
                                        <BreadcrumbPage>{item.name}</BreadcrumbPage>
                                    ) : (
                                        <BreadcrumbLink asChild>
                                            <Link
                                                to={routeHref(routes.category, { categoryId: item.id })}
                                                className="text-muted-foreground hover:text-foreground">
                                                {item.name}
                                            </Link>
                                        </BreadcrumbLink>
                                    )}
                                </BreadcrumbItem>
                            </Fragment>
                        );
                    })}
                </BreadcrumbList>
            </Breadcrumb>
        </>
    );
}
